<template>
  <div data-qa-id="termination-card" :class="$style.terminationCard">
    <!-- Banner -->
    <div :class="[$style.terminationBanner, isTerminating ? $style.terminationBannerTerminating : $style.terminationBannerTerminated]">
      <div :class="$style.terminationBannerContent">
        <div v-if="isTerminating" :class="$style.terminationBannerRow">
          <span :class="$style.terminationBannerText">
            <MdTypography tag="span" variant="body2--regular" color="spirits--800">{{ $t('pages.contract.termination.banner_terminating_date_prefix') }}</MdTypography><MdTypography tag="span" variant="body2--regular" color="spirits--900">{{ formattedDate }}.</MdTypography>
          </span>
          <span :class="$style.terminationBadge">
            <span :class="$style.terminationBadgeIcon">&#9201;</span>
            {{ $t('pages.contract.termination.days_left', { days: daysLeft }) }}
          </span>
        </div>
        <template v-else>
          <MdTypography tag="div" variant="body2--regular" color="spirits--900">
            {{ $t('pages.contract.termination.banner_terminated') }}
          </MdTypography>
        </template>
      </div>
      <img
        :src="illustrationSrc"
        alt=""
        aria-hidden="true"
        :class="[$style.terminationIllustration, !isTerminating && $style.terminationIllustrationTerminated]"
      />
    </div>

    <!-- Logo -->
    <div :class="[$style.imageLogoBackground, isTerminating ? $style.terminationLogoTerminating : $style.terminationLogoTerminated]">
      <span v-if="foundry.logoUrl" :class="[$style.imageLogoSpan, $style.logoSpan]" :style="{ backgroundImage: `url(${foundry.logoUrl})` }" />
      <MdTypography v-else :class="[$style.currentFoundryInitial, $style.logoSpan]" tag="span" color="spirits--900" variant="body1--regular">
        {{ initials }}
      </MdTypography>
    </div>

    <!-- Content -->
    <div :class="$style.terminationContent">
      <!-- Foundry name row -->
      <div :class="$style.terminationNameRow">
        <MdTypography data-qa-id="termination-foundry-name" tag="div" color="spirits--900" variant="heading6--medium">
          {{ foundry.name }}
        </MdTypography>
        <div v-if="showDownload && foundry.isAdmin && isTerminating" :class="$style.terminationDownloadBtn">
          <MdButton
            data-qa-id="termination-download-btn"
            variant="ghost"
            color="darkgrey"
            size="small"
            @click="$emit('download', foundry.foundryId)"
          >
            <MdIconDownload icontitle="disabled" size="sm" slot="icon-start" />
            <span :class="$style.terminationDownloadText">{{ $t('pages.contract.signed.button_label_print') }}</span>
          </MdButton>
        </div>
      </div>

      <!-- Copy row: text + sign button -->
      <div :class="$style.terminationCopyRow">
        <div :class="$style.terminationExplanation">
          <MdTypography tag="div" variant="body1--bold" color="spirits--900">
            {{ isTerminating
              ? $t('pages.contract.termination.terminating_heading')
              : $t('pages.contract.termination.terminated_heading') }}
          </MdTypography>

          <!-- Admin description with learn-more links -->
          <div v-if="foundry.isAdmin" :class="$style.terminationText">
            <MdTypography tag="p" variant="body2--regular" color="spirits--700">
              {{ isTerminating
                ? $t('pages.contract.termination.admin_desc')
                : $t('pages.contract.termination.admin_terminated_desc') }}
            </MdTypography>
            <div :class="$style.terminationLinks">
              <MdTypography tag="p" variant="body2--regular" color="spirits--700">
                {{ isTerminating
                  ? $t('pages.contract.termination.learn_more_termination')
                  : $t('pages.contract.termination.learn_more_termination_terminated') }}
                <a class="mt-link" href="javascript:void(0)" @click="handleLearnMoreClick(LEARN_MORE_TYPE.TERMINATION)">
                  {{ $t('pages.contract.termination.learn_more') }}</a>.
              </MdTypography>
              <MdTypography tag="p" variant="body2--regular" color="spirits--700">
                {{ isTerminating
                  ? $t('pages.contract.termination.learn_more_rejoin')
                  : $t('pages.contract.termination.learn_more_rejoin_terminated') }}
                <a class="mt-link" href="javascript:void(0)" @click="handleLearnMoreClick(LEARN_MORE_TYPE.REJOIN)">
                  {{ $t('pages.contract.termination.learn_more') }}</a>.
              </MdTypography>
            </div>
          </div>

          <!-- Non-admin description -->
          <div v-else :class="$style.terminationText">
            <MdTypography tag="p" variant="body2--regular" color="spirits--700">
              {{ isTerminating
                ? $t('pages.contract.termination.non_admin_terminating_desc')
                : $t('pages.contract.termination.non_admin_terminated_desc') }}
            </MdTypography>
            <MdTypography tag="p" variant="body2--regular" color="spirits--700">
              {{ $t('pages.contract.termination.contact_admin') }}
            </MdTypography>
          </div>
        </div>

        <!-- Sign button -->
        <div :class="$style.terminationButtonWrapper">
          <MdButton
            v-if="foundry.isAdmin"
            data-qa-id="termination-sign-btn"
            @click="$emit('sign', foundry)"
            fullwidth="true"
          >
            <MdIconAgreements icontitle="disabled" size="sm" />&nbsp;
            {{ $t('pages.contract.termination.sign_latest_agreement') }}
          </MdButton>

          <MdTooltip v-else trigger="hover" padding="medium" hidedelay="0" placement="top">
            <div slot="trigger" :class="$style.terminationDisabledBtnWrapper">
              <MdButton
                data-qa-id="termination-sign-btn-disabled"
                disabled
                fullwidth="true"
              >
                <MdIconAgreements icontitle="disabled" size="sm" />&nbsp;
                {{ $t('pages.contract.termination.sign_latest_agreement') }}
              </MdButton>
            </div>
            <span slot="content" class="fou-tooltip__content">
              {{ $t('pages.contract.termination.tooltip_contact_admin') }}
            </span>
          </MdTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MdTypography, MdButton, MdIconDownload, MdIconAgreements, MdTooltip } from '@antiqua-dls/vue-components'
import { daysUntilTermination, formatTerminationDate, openTerminationLearnMoreLink } from 'src/utils/helper/contractTermination'
import { CONTRACT_STATUS, LEARN_MORE_TYPE } from 'src/utils/constants/contract-termination'
import terminatingIllustration from '../foundry-contract-popup/assets/terminating-illustration.svg'
import terminatedIllustration from '../foundry-contract-popup/assets/terminated-illustration.svg'

export default {
  name: 'FoundryTerminationCard',

  components: {
    MdTypography,
    MdButton,
    MdIconDownload,
    MdIconAgreements,
    MdTooltip
  },

  props: {
    foundry: {
      type: Object,
      required: true
    },
    initials: {
      type: String,
      required: true
    },
    showDownload: {
      type: Boolean,
      default: false
    }
  },

  emits: ['sign', 'download'],

  data() {
    return { LEARN_MORE_TYPE }
  },

  methods: {
    handleLearnMoreClick(type) {
      openTerminationLearnMoreLink(type, this.$t.bind(this))
    }
  },

  computed: {
    isTerminating() {
      return this.foundry.contractStatus === CONTRACT_STATUS.TERMINATING
    },
    daysLeft() {
      return daysUntilTermination(this.foundry.terminationDate)
    },
    formattedDate() {
      return formatTerminationDate(this.foundry.terminationDate)
    },
    illustrationSrc() {
      return this.isTerminating ? terminatingIllustration : terminatedIllustration
    }
  }
}
</script>

<style lang="scss" module>
@import "mpl-css/lib/assets/global/variables/breakpoints.scss";

.terminationCard {
  position: relative;
}

.terminationBanner {
  position: relative;
  z-index: 1;
  padding: var(--md-spacing-unit--lg) var(--md-spacing-unit--xl);
  padding-left: 8rem;
  border-top-left-radius: var(--md-border-radius--md);
  border-top-right-radius: var(--md-border-radius--md);
  display: flex;
  align-items: flex-end;
  overflow: hidden;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    padding-left: var(--md-spacing-unit--xl);
  }
}

.terminationBannerTerminating {
  background-color: var(--md-color--spirits--50);
}

.terminationBannerTerminated {
  background-color: var(--md-color--spirits--50);
}

.terminationBannerContent {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
}

.terminationBannerRow {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.terminationBannerText {
  display: inline;
  white-space: nowrap;
}

.terminationBannerMeta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.terminationDateLine {
  display: inline;
  white-space: nowrap;
}

.terminationBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--md-spacing-unit--2xs);
  background-color: var(--md-color--red-babe--100);
  color: var(--md-color--red-babe--700);
  font-family: 'Helvetica Now Micro W05 Medium', sans-serif;
  font-size: 0.688rem;
  font-weight: 500;
  line-height: 1rem;
  padding: var(--md-spacing-unit--3xs) var(--md-spacing-unit--xs);
  border-radius: var(--md-border-radius--sm);
  white-space: nowrap;
  height: 1.5rem;
  box-sizing: border-box;
}

.terminationBadgeIcon {
  font-size: 0.75rem;
  line-height: 1;
}

.terminationIllustration {
  position: absolute;
  right: -0.5rem;
  top: -0.3rem;
  width: auto;
  z-index: 0;
  pointer-events: none;
  height: 110%;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    display: none;
  }
}

.terminationIllustrationTerminated {
  height: 180%;
  top: -0.3rem;
  right: -0.3rem;
}

.imageLogoBackground {
  width: 5.5rem;
  height: 5.5rem;
  position: absolute;
  left: 1.625rem;
  z-index: 2;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    width: 4.125rem;
    height: 4.125rem;
  }
}

.imageLogoSpan {
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;
  display: block;
  width: 5.5rem;
  height: 5.5rem;
  border: 0.25rem solid var(--md-color--white);
  border-radius: 2.75rem;
}

.currentFoundryInitial {
  background-color: var(--md-color--spirits--200);
  width: 5.5rem;
  height: 5.5rem;
  border: var(--md-spacing-unit--2xs) solid var(--md-color--white);
  border-radius: 2.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logoSpan {
  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    width: 4.125rem;
    height: 4.125rem;
  }
}

.terminationLogoTerminating {
  top: 2.25rem;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    top: 2.25rem;
  }
}

.terminationLogoTerminated {
  top: 2.25rem;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    top: 2.25rem;
  }
}

.terminationContent {
  padding: 1rem var(--md-spacing-unit--xl) var(--md-spacing-unit--xl);
  padding-left: 8rem;
  display: flex;
  flex-direction: column;
  gap: var(--md-spacing-unit--xl);

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    padding-left: var(--md-spacing-unit--xl);
    padding-top: 3rem;
  }
}

.terminationNameRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.terminationDownloadBtn {
  md-button::part(md-button) {
    color: var(--md-color--spirits--700);
    text-decoration: none;
  }
}

.terminationDownloadText {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}

.terminationCopyRow {
  display: flex;
  gap: var(--md-spacing-unit--xl);
  align-items: flex-end;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    flex-direction: column;
    align-items: stretch;
  }
}

.terminationExplanation {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.terminationText {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  p {
    margin: 0;
    line-height: 1rem;
  }
}

.terminationLinks {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.terminationButtonWrapper {
  flex-shrink: 0;
  width: 12.5rem;

  @media screen and (max-width: map-get($breakpoints, 'tab')) {
    width: 100%;
  }
}

.terminationDisabledBtnWrapper {
  width: 100%;
}
</style>
